import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowRight, Factory, Leaf, Pickaxe, Building2, Heart, CheckCircle } from 'lucide-react';

const SectorsPage = () => {
  const sectors = [
    {
      icon: Factory,
      title: 'Industry',
      description: 'Supporting manufacturing and industrial operations with reliable procurement of equipment, raw materials, and spare parts.',
      image: '/8.jpeg',
      capabilities: [
        'Industrial machinery sourcing',
        'Spare parts procurement',
        'Raw materials supply',
        'Equipment maintenance supplies',
      ],
    },
    {
      icon: Leaf,
      title: 'Agro-Industry',
      description: 'Comprehensive sourcing solutions for agricultural equipment, inputs, and processing machinery to boost productivity.',
      image: '/7.jpeg',
      capabilities: [
        'Agricultural equipment',
        'Seeds and fertilizers',
        'Processing machinery',
        'Irrigation systems',
      ],
    },
    {
      icon: Pickaxe,
      title: 'Mining',
      description: 'Specialized procurement for mining operations including heavy equipment, safety gear, and operational supplies.',
      image: '/9.jpeg',
      capabilities: [
        'Heavy mining equipment',
        'Safety equipment and PPE',
        'Operational consumables',
        'Specialized tools and machinery',
      ],
    },
    {
      icon: Building2,
      title: 'Construction',
      description: 'End-to-end procurement for construction projects from building materials to heavy machinery and safety equipment.',
      image: '/1.jpeg',
      capabilities: [
        'Construction materials',
        'Heavy machinery',
        'Tools and equipment',
        'Safety and compliance gear',
      ],
    },
    {
      icon: Heart,
      title: 'NGO & Institutions',
      description: 'Tailored procurement services for humanitarian organizations and institutions with focus on compliance and transparency.',
      image: '/3.jpeg',
      capabilities: [
        'Humanitarian supplies',
        'Medical equipment',
        'Educational materials',
        'Institutional furniture',
      ],
    },
  ];

  return (
    <div data-testid="sectors-page">
      {/* Hero Section */}
      <section className="relative bg-[#0F172A] pt-32 pb-20 noise-overlay">
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-3xl">
            <span className="text-[#0EA5E9] text-sm font-medium uppercase tracking-widest">
              Sectors We Serve
            </span>
            <h1 className="font-['Outfit'] text-4xl sm:text-5xl lg:text-6xl font-bold text-white mt-4 mb-6">
              Industry-Specific Procurement Expertise
            </h1>
            <p className="text-slate-400 text-lg leading-relaxed">
              We understand the unique requirements of different industries and provide 
              tailored procurement solutions that meet sector-specific standards.
            </p>
          </div>
        </div>
      </section>

      {/* Sectors Grid */}
      <section data-testid="sectors-grid" className="bg-[#F8FAFC] py-20 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="space-y-16">
            {sectors.map((sector, index) => (
              <div
                key={sector.title}
                data-testid={`sector-detail-${index}`}
                className={`grid lg:grid-cols-2 gap-12 items-center ${
                  index % 2 === 1 ? 'lg:grid-flow-dense' : ''
                }`}
              >
                {/* Image */}
                <div className={`relative ${index % 2 === 1 ? 'lg:col-start-2' : ''}`}>
                  <div className="absolute -inset-4 border border-slate-200 -z-10"></div>
                  <img
                    src={sector.image}
                    alt={sector.title}
                    className="w-full h-[350px] object-cover"
                  />
                  <div className="absolute top-4 left-4 w-14 h-14 bg-[#0F172A] flex items-center justify-center">
                    <sector.icon className="text-white" size={28} />
                  </div>
                </div>

                {/* Content */}
                <div className={index % 2 === 1 ? 'lg:col-start-1 lg:row-start-1' : ''}>
                  <span className="font-['JetBrains_Mono'] text-[#0EA5E9] text-sm">
                    0{index + 1}
                  </span>
                  <h2 className="font-['Outfit'] text-3xl md:text-4xl font-semibold text-[#0F172A] mt-2 mb-4">
                    {sector.title}
                  </h2>
                  <p className="text-slate-600 text-lg leading-relaxed mb-6">
                    {sector.description}
                  </p>
                  <ul className="space-y-3 mb-8">
                    {sector.capabilities.map((capability, i) => (
                      <li key={i} className="flex items-center gap-3">
                        <CheckCircle className="text-[#0EA5E9] flex-shrink-0" size={18} />
                        <span className="text-slate-600">{capability}</span>
                      </li>
                    ))}
                  </ul>
                  <Link to="/contact">
                    <Button
                      data-testid={`sector-cta-${index}`}
                      className="bg-[#0F172A] hover:bg-[#1E293B] text-white rounded-none px-6 py-3 font-medium transition-colors duration-300"
                    >
                      Discuss Your Needs
                      <ArrowRight className="ml-2" size={16} />
                    </Button>
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="bg-white py-16 border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            {[
              { value: '5', label: 'Key Sectors' },
              { value: '200+', label: 'Projects Completed' },
              { value: '30+', label: 'Countries Served' },
              { value: '100%', label: 'Client Retention' },
            ].map((stat, index) => (
              <div key={stat.label}>
                <div className="font-['Outfit'] text-4xl md:text-5xl font-bold text-[#0F172A] mb-2">
                  {stat.value}
                </div>
                <div className="text-slate-500 text-sm uppercase tracking-wider">
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Expertise Section */}
      <section className="bg-[#0F172A] py-20 md:py-32 noise-overlay">
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center max-w-3xl mx-auto">
            <span className="text-[#0EA5E9] text-sm font-medium uppercase tracking-widest">
              Our Expertise
            </span>
            <h2 className="font-['Outfit'] text-3xl md:text-4xl font-semibold text-white mt-4 mb-6">
              Deep Industry Knowledge
            </h2>
            <p className="text-slate-400 text-lg leading-relaxed mb-12">
              Our team combines procurement expertise with industry-specific knowledge, 
              ensuring we understand the technical requirements, regulatory standards, 
              and operational challenges unique to each sector.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                title: 'Technical Expertise',
                description: 'Our specialists understand the technical specifications required in each industry.',
              },
              {
                title: 'Regulatory Compliance',
                description: 'We ensure all procured goods meet industry-specific regulatory standards.',
              },
              {
                title: 'Quality Assurance',
                description: 'Rigorous quality control processes tailored to sector requirements.',
              },
            ].map((item, index) => (
              <div
                key={item.title}
                className="bg-[#1E293B] border border-slate-700 p-8"
              >
                <h3 className="font-['Outfit'] text-xl font-semibold text-white mb-3">
                  {item.title}
                </h3>
                <p className="text-slate-400 leading-relaxed">
                  {item.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-[#0EA5E9] py-16">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-['Outfit'] text-2xl md:text-3xl font-semibold text-white mb-4">
            Don't See Your Industry?
          </h2>
          <p className="text-white/80 text-lg mb-8 max-w-xl mx-auto">
            We work with businesses across various sectors. Contact us to discuss your specific needs.
          </p>
          <Link to="/contact">
            <Button
              data-testid="sectors-cta-btn"
              className="bg-white text-[#0EA5E9] hover:bg-[#F8FAFC] rounded-none px-8 py-4 font-medium transition-colors duration-300"
            >
              Contact Us
              <ArrowRight className="ml-2" size={18} />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default SectorsPage;
